/**
 * Copyright Highway9 Networks Inc.
 *
 * Redux state for the device UE report (pre-bucketed usage + connectivity).
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "..";
import { subscriberService } from "~/services";
import { DeviceReport } from "~/types/deviceReport";

export type FetchUEReportArgs = {
  deviceId: string;
  /** Inclusive range start, Unix epoch seconds. */
  startTime: number;
  /** Inclusive range end, Unix epoch seconds (already resolved for live intervals). */
  endTime: number;
  aggregationSeconds: number;
  /** UTC offset in minutes (`moment.tz(tz).utcOffset()`). */
  timezoneOffset: number;
};

interface UEReportState {
  report: DeviceReport | null;
  loading: boolean;
  fetching: boolean;
  error: string | null;
  deviceId: string | null;
  /** Latest in-flight fetch; used to ignore stale fulfilled/rejected. */
  activeRequestId: string | null;
}

export const initialState: UEReportState = {
  report: null,
  loading: true,
  fetching: true,
  error: null,
  deviceId: null,
  activeRequestId: null,
};

export const fetchUEReport = createAsyncThunk(
  "ueReport/fetchUEReport",
  async (
    { deviceId, startTime, endTime, aggregationSeconds, timezoneOffset }: FetchUEReportArgs,
    { rejectWithValue }
  ) => {
    try {
      const reports = await subscriberService.getDeviceReport({
        ids: [deviceId],
        interval: { startTime, endTime },
        bucketSeconds: aggregationSeconds,
        timezone: timezoneOffset,
        sections: ["connectivity", "usage"],
        include: ["radioStats"],
      });
      return reports[0] ?? null;
    } catch (err) {
      console.error("Failed to load UE report data", err);
      return rejectWithValue(
        err instanceof Error ? err.message : "Unable to load UE report data."
      );
    }
  }
);

const ueReportSlice = createSlice({
  name: "ueReport",
  initialState,
  reducers: {
    clearUEReport: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUEReport.pending, (state, action) => {
      state.fetching = true;
      state.activeRequestId = action.meta.requestId;
      // Full skeleton only when switching devices (same pattern as edge/radio metrics
      // deciding loading in pending from arg — here derived from stored deviceId).
      if (state.deviceId !== action.meta.arg.deviceId) {
        state.loading = true;
        state.error = null;
        state.report = null;
        state.deviceId = action.meta.arg.deviceId;
      }
    });
    builder.addCase(fetchUEReport.fulfilled, (state, action) => {
      // Drop superseded results if an older request finishes after a newer one started.
      if (action.meta.requestId !== state.activeRequestId) {
        return;
      }
      state.report = action.payload;
      state.error = null;
      state.loading = false;
      state.fetching = false;
      state.deviceId = action.meta.arg.deviceId;
    });
    builder.addCase(fetchUEReport.rejected, (state, action) => {
      // Ignore superseded requests so a newer fetch can finish cleanly.
      if (action.meta.requestId !== state.activeRequestId) {
        return;
      }
      state.report = null;
      state.error =
        typeof action.payload === "string"
          ? action.payload
          : "Unable to load UE report data.";
      state.loading = false;
      state.fetching = false;
    });
  },
});

export const ueReportActions = ueReportSlice.actions;

export const selectUEReport = (state: RootState) => state.ueReport.report;
export const selectUEReportLoading = (state: RootState) => state.ueReport.loading;
export const selectUEReportFetching = (state: RootState) => state.ueReport.fetching;
export const selectUEReportError = (state: RootState) => state.ueReport.error;

export default ueReportSlice.reducer;
